import { useEffect, useLayoutEffect } from 'react';

export function useBelotDealAnimation({
  engineState,
  sessionStarted,
  humanSeat,
  handSlotRefs,
  /** @type {React.MutableRefObject<Record<number, HTMLElement|null>>|undefined} */
  seatRefs,
  seatCardSlotRefs,
  deckRef,
  play,
  dealCardSoundVol,
  dealSeatOrder,
  dealHeroStaggerMs,
  dealBetweenSeatsMs,
  dealAnimGenRef,
  dealFlightAbortRef,
  lastBumpedDealGenRef,
  setRoundDealAnimActive,
  setSeatDealSlotRevealed,
  setDealVisualEpoch,
  setDealFinishedNonce,
  flyHeroDealCardFromDeck,
}) {
  const heroDealTriggerKey = (() => {
    if (!sessionStarted || engineState.phase !== 'auction') return '';
    const raw = engineState.hands?.[humanSeat];
    if (!raw || raw.length !== 8) return '';
    const sorted = [...raw].sort((a, b) => a - b);
    return `${engineState.roundIndex}-${sorted.join(',')}`;
  })();

  useEffect(() => {
    if (engineState.phase !== 'auction') {
      setRoundDealAnimActive(false);
      setSeatDealSlotRevealed(null);
    }
  }, [engineState.phase, setRoundDealAnimActive, setSeatDealSlotRevealed]);

  useLayoutEffect(() => {
    if (!heroDealTriggerKey) return undefined;
    const gen = ++dealAnimGenRef.current;
    let cancelled = false;
    dealFlightAbortRef.current = false;
    setRoundDealAnimActive(true);
    setDealVisualEpoch((e) => e + 1);

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (prefersReduced) {
      setSeatDealSlotRevealed(null);
      for (let i = 0; i < 32; i += 1) {
        window.setTimeout(() => play('card-deal', dealCardSoundVol), i * 42);
      }
      queueMicrotask(() => {
        if (gen !== dealAnimGenRef.current) return;
        setSeatDealSlotRevealed(null);
        setRoundDealAnimActive(false);
        if (lastBumpedDealGenRef.current !== gen) {
          lastBumpedDealGenRef.current = gen;
          setDealFinishedNonce((n) => n + 1);
        }
      });
      return () => {
        cancelled = true;
        dealFlightAbortRef.current = true;
        setRoundDealAnimActive(false);
        setSeatDealSlotRevealed(null);
      };
    }

    setSeatDealSlotRevealed(Array.from({ length: 4 }, () => Array(8).fill(false)));

    const run = async () => {
      try {
        await new Promise((r) => {
          requestAnimationFrame(() => requestAnimationFrame(r));
        });
        if (cancelled || gen !== dealAnimGenRef.current) return;

        const deckEl = deckRef.current;
        if (!deckEl) {
          for (let k = 0; k < 32; k += 1) {
            if (cancelled || gen !== dealAnimGenRef.current) break;
            play('card-deal', dealCardSoundVol);
            await new Promise((r) => setTimeout(r, 42));
          }
          return;
        }

        for (let si = 0; si < dealSeatOrder.length; si += 1) {
          const seat = dealSeatOrder[si];
          if (cancelled || gen !== dealAnimGenRef.current || dealFlightAbortRef.current) break;
          for (let c = 0; c < 8; c += 1) {
            if (cancelled || gen !== dealAnimGenRef.current || dealFlightAbortRef.current) break;
            const seatEl = seatRefs?.current?.[seat];
            const targetEl =
              seat === humanSeat
                ? handSlotRefs[c]?.current
                : seatEl ?? seatCardSlotRefs?.[seat]?.[c]?.current;
            const cardSize = seat === humanSeat ? 'md' : 'sm';
            if (!targetEl) {
              await new Promise((r) => setTimeout(r, dealHeroStaggerMs));
              continue;
            }
            try {
              await flyHeroDealCardFromDeck(deckEl, targetEl, () => play('card-deal', dealCardSoundVol), cardSize);
            } catch {
              /* ignore */
            }
            if (!cancelled && gen === dealAnimGenRef.current && !dealFlightAbortRef.current) {
              setSeatDealSlotRevealed((prev) => {
                if (!prev) return prev;
                const next = prev.map((row) => [...row]);
                if (next[seat]) next[seat][c] = true;
                return next;
              });
            }
            if (c < 7) await new Promise((r) => setTimeout(r, dealHeroStaggerMs));
          }
          if (si < dealSeatOrder.length - 1) await new Promise((r) => setTimeout(r, dealBetweenSeatsMs));
        }
      } finally {
        if (gen === dealAnimGenRef.current) {
          setSeatDealSlotRevealed(null);
          setRoundDealAnimActive(false);
          if (lastBumpedDealGenRef.current !== gen) {
            lastBumpedDealGenRef.current = gen;
            setDealFinishedNonce((n) => n + 1);
          }
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
      dealFlightAbortRef.current = true;
      setSeatDealSlotRevealed(null);
    };
  }, [
    heroDealTriggerKey,
    dealAnimGenRef,
    dealFlightAbortRef,
    setRoundDealAnimActive,
    setDealVisualEpoch,
    setSeatDealSlotRevealed,
    play,
    dealCardSoundVol,
    lastBumpedDealGenRef,
    setDealFinishedNonce,
    deckRef,
    dealSeatOrder,
    humanSeat,
    handSlotRefs,
    seatRefs,
    seatCardSlotRefs,
    dealHeroStaggerMs,
    flyHeroDealCardFromDeck,
    dealBetweenSeatsMs,
  ]);
}

